import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Car, DollarSign, TrendingUp, Shield, CheckCircle, MapPin, Sparkles, Clock, ArrowRight, Search, CalendarCheck, Wrench, ShoppingCart, Star, Quote } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { getAvailableCarsForRent, getAvailableCarsForSale, getApprovedReviews, type Car as CarType } from '../lib/supabase';
import toast from 'react-hot-toast';
import DynamicSearchBox from '../components/home/DynamicSearchBox';
import { useCurrency } from '../contexts/CurrencyContext';

export default function HomePage() {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [featuredCars, setFeaturedCars] = useState<CarType[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<any[]>([]);

  // Fetch featured cars from Supabase
  useEffect(() => {
    async function fetchCars() {
      try {
        // Feature cars offered either way — a sale-only car still belongs on the front page.
        const [forRent, forSale] = await Promise.all([
          getAvailableCarsForRent(),
          getAvailableCarsForSale(),
        ]);
        const merged = [...forRent, ...forSale].reduce((acc, car) => {
          if (!acc.some(c => c.id === car.id)) acc.push(car);
          return acc;
        }, [] as CarType[]);
        merged.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setFeaturedCars(merged.slice(0, 6));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching cars:', error);
        toast.error('Failed to load cars');
        setLoading(false);
      }
    }
    fetchCars();
  }, []);

  // Fetch approved customer reviews for the testimonials section
  useEffect(() => {
    getApprovedReviews(6)
      .then(setReviews)
      .catch((err) => console.error('Error fetching reviews:', err));
  }, []);

  const features = [
    {
      icon: <Car className="w-8 h-8" />,
      title: t('home.features.wideSelection'),
      description: t('home.features.wideSelectionDesc'),
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: t('home.features.trustedSafe'),
      description: t('home.features.trustedSafeDesc'),
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: t('home.features.bestPrices'),
      description: t('home.features.bestPricesDesc'),
    },
  ];

  const categories = [
    { name: t('categories.suv'), image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=600&q=80', query: 'suv' },
    { name: t('categories.sedan'), image: 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=600&q=80', query: 'sedan' },
    { name: t('categories.pickup'), image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=600&q=80', query: 'pickup' },
    { name: t('categories.luxury'), image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80', query: 'luxury' },
  ];

  const trustBadges = [
    { icon: <CheckCircle className="w-4 h-4 text-ekami-gold-400" />, label: 'Verified vehicles' },
    { icon: <Sparkles className="w-4 h-4 text-ekami-gold-400" />, label: 'AI-powered assistance' },
    { icon: <MapPin className="w-4 h-4 text-ekami-gold-400" />, label: 'Across Cameroon' },
    { icon: <Clock className="w-4 h-4 text-ekami-gold-400" />, label: '24/7 WhatsApp support' },
  ];

  const services = [
    { icon: <Car className="w-7 h-7" />, title: 'Rent a Car', description: 'Daily, weekly & monthly rentals across Cameroon.', to: '/rent' },
    { icon: <ShoppingCart className="w-7 h-7" />, title: 'Buy a Car', description: 'Quality, verified vehicles for sale at fair prices.', to: '/buy' },
    { icon: <DollarSign className="w-7 h-7" />, title: 'Sell Your Car', description: 'Get a fair offer and sell your car with ease.', to: '/sell' },
    { icon: <Wrench className="w-7 h-7" />, title: 'Repairs & Service', description: 'Professional maintenance by certified mechanics.', to: '/repairs' },
  ];

  const steps = [
    {
      icon: <Search className="w-7 h-7" />,
      title: 'Browse & Search',
      description: 'Explore our verified fleet or ask the AI assistant to find the perfect car for your trip.',
    },
    {
      icon: <CalendarCheck className="w-7 h-7" />,
      title: 'Book or Enquire',
      description: 'Reserve your dates online or reach us instantly on WhatsApp — no account required.',
    },
    {
      icon: <Car className="w-7 h-7" />,
      title: 'Pick Up & Drive',
      description: 'Collect your car at a convenient location across Cameroon and hit the road.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-ekami-charcoal-900 via-ekami-charcoal-800 to-ekami-silver-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-white opacity-5"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        {/* Luxury shine effect */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-ekami-silver-400/10 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-ekami-silver-200">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/rent"
                className="px-10 py-5 bg-gradient-to-r from-gray-100 to-gray-200 text-ekami-charcoal-900 rounded-2xl font-bold hover:from-gray-200 hover:to-gray-300 transition-all shadow-2xl hover:shadow-ekami-silver-400/50 transform hover:-translate-y-1 hover:scale-105 border-2 border-white/50"
              >
                {t('hero.searchCars')}
              </Link>
              <button
                onClick={() => window.dispatchEvent(new Event('ekami:open-chat'))}
                className="px-10 py-5 bg-gradient-to-r from-ekami-gold-500 to-ekami-gold-600 text-white rounded-2xl font-bold hover:from-ekami-gold-600 hover:to-ekami-gold-700 transition-all shadow-2xl hover:shadow-ekami-gold-500/50 transform hover:-translate-y-1 hover:scale-105 flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                {t('hero.talkToAI')}
              </button>
            </div>

            {/* Trust badges */}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-ekami-silver-200">
              {trustBadges.map((badge) => (
                <span key={badge.label} className="flex items-center gap-2">
                  {badge.icon}
                  {badge.label}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Dynamic Search Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 max-w-5xl mx-auto"
          >
            <DynamicSearchBox />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gradient-to-b from-ekami-silver-50 to-white dark:from-ekami-charcoal-900 dark:to-ekami-charcoal-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-ekami-silver-200 to-ekami-silver-300 dark:from-ekami-charcoal-700 dark:to-ekami-charcoal-600 text-ekami-charcoal-700 dark:text-ekami-silver-200 rounded-full mb-4 shadow-lg">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-ekami-charcoal-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-ekami-charcoal-600 dark:text-ekami-silver-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 bg-gradient-to-b from-ekami-silver-50 to-white dark:from-ekami-charcoal-800 dark:to-ekami-charcoal-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-ekami-charcoal-900 dark:text-white mb-4">
              Everything Automotive, One Place
            </h2>
            <p className="text-xl text-ekami-charcoal-600 dark:text-ekami-silver-300">
              Rent, buy, sell, or service your car with Ekami Auto
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={service.to} className="group block card h-full transform hover:-translate-y-1 transition-all">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-ekami-gold-500 to-ekami-gold-600 text-white mb-4 shadow-lg group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-ekami-charcoal-900 dark:text-white">
                    {service.title}
                  </h3>
                  <p className="text-ekami-charcoal-600 dark:text-ekami-silver-400 mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-ekami-gold-600 dark:text-ekami-gold-400 font-semibold">
                    Learn more
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Cars */}
      <section className="py-16 bg-white dark:bg-ekami-charcoal-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-ekami-charcoal-900 dark:text-white mb-4">
              {t('home.featuredCars')}
            </h2>
            <p className="text-xl text-ekami-charcoal-600 dark:text-ekami-silver-300">
              {t('home.featuredCarsSubtitle')}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-48 bg-gray-300 dark:bg-gray-700 rounded-2xl mb-4"></div>
                  <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : featuredCars.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCars.map((car, index) => {
                // A car with no usable daily rate is shown at its sale price rather than "0 /day".
                const showAsRental = car.available_for_rent && (car.price_rent_daily || 0) > 0;
                return (
                <motion.div
                  key={car.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/cars/${car.slug || car.id}`} className="block card group">
                    {/* Car Image */}
                    <div className="relative h-48 rounded-2xl overflow-hidden mb-4">
                      <img
                        src={car.images?.[0] || 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800'}
                        alt={`${car.make} ${car.model}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-3 right-3 bg-ekami-gold-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {car.body_type}
                      </div>
                    </div>

                    {/* Car Info */}
                    <h3 className="text-xl font-bold text-ekami-charcoal-900 dark:text-white mb-2">
                      {car.make} {car.model}
                    </h3>
                    <p className="text-ekami-charcoal-600 dark:text-ekami-silver-400 text-sm mb-3">
                      {car.year} • {car.transmission} • {car.fuel_type}
                    </p>

                    {/* Pricing */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-ekami-charcoal-500 dark:text-ekami-silver-500">
                          {showAsRental ? t('home.from') : t('home.forSale')}
                        </p>
                        <p className="text-2xl font-bold text-ekami-gold-600">
                          {formatPrice(showAsRental ? (car.price_rent_daily || 0) : (car.price_sale || 0))}
                          {showAsRental && (
                            <span className="text-sm font-normal text-ekami-charcoal-500 dark:text-ekami-silver-500">{t('home.perDay')}</span>
                          )}
                        </p>
                      </div>
                      <div className="px-4 py-2 bg-gradient-to-r from-ekami-charcoal-700 to-ekami-charcoal-800 text-white rounded-xl font-semibold text-sm group-hover:from-ekami-gold-500 group-hover:to-ekami-gold-600 transition-all">
                        {t('home.viewDetails')}
                      </div>
                    </div>

                    {/* Features */}
                    {car.features && car.features.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {car.features.slice(0, 3).map((feature, i) => (
                          <span
                            key={i}
                            className="text-xs px-2 py-1 bg-ekami-silver-100 dark:bg-ekami-charcoal-700 text-ekami-charcoal-700 dark:text-ekami-silver-300 rounded-lg"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}
                  </Link>
                </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Car className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-xl text-gray-600 dark:text-gray-400">No cars available at the moment</p>
            </div>
          )}

          {/* View All Button */}
          {featuredCars.length > 0 && (
            <div className="text-center mt-12 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/rent"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-ekami-charcoal-700 to-ekami-charcoal-800 text-white rounded-2xl font-semibold hover:from-ekami-gold-500 hover:to-ekami-gold-600 transition-all shadow-xl transform hover:-translate-y-1"
              >
                Cars for Rent
                <TrendingUp className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/buy"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-ekami-charcoal-700 to-ekami-charcoal-800 text-white rounded-2xl font-semibold hover:from-ekami-gold-500 hover:to-ekami-gold-600 transition-all shadow-xl transform hover:-translate-y-1"
              >
                Cars for Sale
                <ShoppingCart className="ml-2 w-5 h-5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gradient-to-b from-ekami-gold-50/40 to-white dark:from-ekami-charcoal-800 dark:to-ekami-charcoal-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-ekami-charcoal-900 dark:text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-ekami-charcoal-600 dark:text-ekami-silver-300">
              Get on the road in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative text-center px-4"
              >
                <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-ekami-gold-500 to-ekami-gold-600 text-white shadow-lg mb-5">
                  {step.icon}
                  <span className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center rounded-full bg-ekami-charcoal-900 text-white text-sm font-bold border-2 border-white dark:border-ekami-charcoal-900">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-2 text-ekami-charcoal-900 dark:text-white">
                  {step.title}
                </h3>
                <p className="text-ekami-charcoal-600 dark:text-ekami-silver-400">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-gradient-to-b from-ekami-silver-50 to-white dark:from-ekami-charcoal-900 dark:to-ekami-charcoal-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-ekami-charcoal-900 dark:text-white mb-4">
              Browse by Category
            </h2>
            <p className="text-xl text-ekami-charcoal-600 dark:text-ekami-silver-300">
              Find the perfect car for your needs
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/rent?category=${category.query}`}
                  className="group block relative h-48 rounded-2xl overflow-hidden shadow-lg"
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <h3 className="text-xl font-bold text-white">{category.name}</h3>
                    <span className="flex items-center gap-1 text-sm font-semibold text-ekami-gold-300 translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all">
                      Explore <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials (shown only when there are approved reviews) */}
      {reviews.length > 0 && (
        <section className="py-16 bg-white dark:bg-ekami-charcoal-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-ekami-charcoal-900 dark:text-white mb-4">
                What Our Customers Say
              </h2>
              <p className="text-xl text-ekami-charcoal-600 dark:text-ekami-silver-300">
                Real reviews from Ekami Auto customers
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card flex flex-col"
                >
                  <Quote className="w-8 h-8 text-ekami-gold-400/50 mb-3" />
                  <div className="flex mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-4 h-4 ${s <= review.rating ? 'text-ekami-gold-500 fill-current' : 'text-ekami-silver-300 dark:text-ekami-charcoal-600'}`}
                      />
                    ))}
                  </div>
                  {review.title && (
                    <h3 className="font-bold text-ekami-charcoal-900 dark:text-white mb-1">
                      {review.title}
                    </h3>
                  )}
                  <p className="text-ekami-charcoal-600 dark:text-ekami-silver-400 mb-4 flex-1">
                    “{review.comment}”
                  </p>
                  <p className="font-semibold text-ekami-charcoal-900 dark:text-white">
                    — {review.user_name || 'Verified customer'}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-ekami-charcoal-900 via-ekami-silver-800 to-ekami-charcoal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ekami-gold-500/10 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-ekami-silver-200">
            Join thousands of satisfied customers across Cameroon
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/rent"
              className="px-10 py-5 bg-gradient-to-r from-gray-100 to-gray-200 text-ekami-charcoal-900 rounded-2xl font-bold hover:from-gray-200 hover:to-gray-300 transition-all shadow-2xl transform hover:-translate-y-1 hover:scale-105 border-2 border-white/50"
            >
              Rent a Car
            </Link>
            <Link
              to="/sell"
              className="px-10 py-5 bg-gradient-to-r from-ekami-gold-500 to-ekami-gold-600 text-white rounded-2xl font-bold hover:from-ekami-gold-600 hover:to-ekami-gold-700 transition-all shadow-2xl transform hover:-translate-y-1 hover:scale-105"
            >
              Sell Your Car
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
